import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dispatch } from 'redux';
import {
  Dialog,
  DialogSurface,
  Button,
  FluentProvider,
  Input,
  InputOnChangeData,
  Tab,
  TabList,
  makeStyles,
  tokens,
  webDarkTheme,
  webLightTheme,
  Text,
  Caption1,
  Body2
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  Search24Regular,
  Book20Regular
} from '@fluentui/react-icons';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';

import { promptGalleryService, PromptGalleryItem } from '../services/promptGalleryService';
import { useMCAPSCopilotTelemetry } from '../../utils/mcapsCopilotTelemetry';

// Custom Sparkle Icon Component
const SparkleIcon: React.FC = () => (
  <svg aria-label="Agent" fill="currentColor" role="img" width="24px" height="24px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.9175 3L10.3321 5.46119L7.3606 20.9129C7.56621 20.9693 7.7809 20.9993 7.99993 21L8.00812 21H15.9999L16.0003 20.9975C16.7633 20.9618 17.4593 20.5404 17.8435 19.8746L18.5101 18.7196L18.4817 18.7536L18.4899 18.7311L21.719 13.1227C22.1191 12.4277 22.1191 11.5723 21.719 10.8773L17.8956 4.23675C17.6754 3.85417 17.3613 3.54498 16.9919 3.33144L16.9667 3.31707C16.673 3.15184 16.3454 3.04659 16.0028 3.01229L15.9999 3H13.9175Z" fill="url(#paint0_radial_769_23819)"></path>
    <path d="M13.9175 3L10.3321 5.46119L7.3606 20.9129C7.56621 20.9693 7.7809 20.9993 7.99993 21L8.00812 21H15.9999L16.0003 20.9975C16.7633 20.9618 17.4593 20.5404 17.8435 19.8746L18.5101 18.7196L18.4817 18.7536L18.4899 18.7311L21.719 13.1227C22.1191 12.4277 22.1191 11.5723 21.719 10.8773L17.8956 4.23675C17.6754 3.85417 17.3613 3.54498 16.9919 3.33144L16.9667 3.31707C16.673 3.15184 16.3454 3.04659 16.0028 3.01229L15.9999 3H13.9175Z" fill="url(#paint1_radial_769_23819)" fillOpacity="0.5"></path>
    <path d="M13.7759 4.67398L9.9532 19.4323C9.75563 20.329 8.95619 21 8.00003 21C7.96159 21 7.9234 20.9989 7.88549 20.9968C7.49153 20.977 7.11434 20.863 6.78256 20.6711L6.75844 20.657C6.40995 20.4501 6.11276 20.1568 5.90022 19.7969C5.88605 19.7729 5.87226 19.7487 5.85885 19.7241L2.24422 13.0972C1.86907 12.4095 1.87868 11.5761 2.26958 10.8972L6.16736 4.12733C6.24571 3.99125 6.33707 3.86536 6.43937 3.75085H6.42709C6.82614 3.3042 7.39153 3.03091 7.99949 3.00247L8.00003 3H15.75C14.7565 3 13.9316 3.72448 13.7759 4.67398Z" fill="url(#paint2_radial_769_23819)"></path>
    <path d="M13.7759 4.67398L9.9532 19.4323C9.75563 20.329 8.95619 21 8.00003 21C7.96159 21 7.9234 20.9989 7.88549 20.9968C7.49153 20.977 7.11434 20.863 6.78256 20.6711L6.75844 20.657C6.40995 20.4501 6.11276 20.1568 5.90022 19.7969C5.88605 19.7729 5.87226 19.7487 5.85885 19.7241L2.24422 13.0972C1.86907 12.4095 1.87868 11.5761 2.26958 10.8972L6.16736 4.12733C6.24571 3.99125 6.33707 3.86536 6.43937 3.75085H6.42709C6.82614 3.3042 7.39153 3.03091 7.99949 3.00247L8.00003 3H15.75C14.7565 3 13.9316 3.72448 13.7759 4.67398Z" fill="url(#paint3_linear_769_23819)" fillOpacity="0.4"></path>
    <path d="M15.834 3.00173C15.8062 3.00058 15.7782 3 15.75 3H15.8344L15.834 3.00173Z" fill="url(#paint4_radial_769_23819)"></path>
    <path d="M15.834 3.00173C15.8062 3.00058 15.7782 3 15.75 3H15.8344L15.834 3.00173Z" fill="url(#paint5_linear_769_23819)" fillOpacity="0.4"></path>
    <defs>
      <radialGradient id="paint0_radial_769_23819" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(13.8929 25.8857) rotate(-87.747) scale(26.2489 22.3637)">
        <stop stopColor="#FFC470"></stop>
        <stop offset="0.251092" stopColor="#FF835C"></stop>
        <stop offset="0.584326" stopColor="#F24A9D"></stop>
        <stop offset="0.871234" stopColor="#B339F0"></stop>
        <stop offset="1" stopColor="#C354FF"></stop>
      </radialGradient>
      <radialGradient id="paint1_radial_769_23819" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(20.3486 19.2) rotate(-159.917) scale(25.461 21.8263)">
        <stop offset="0.708817" stopColor="#FFB357" stopOpacity="0"></stop>
        <stop offset="0.941624" stopColor="#FFB357"></stop>
      </radialGradient>
      <radialGradient id="paint2_radial_769_23819" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(20.3486 19.2) rotate(-159.917) scale(25.461 21.8263)">
        <stop offset="0.221888" stopColor="#4E46E2"></stop>
        <stop offset="0.577558" stopColor="#625DF6"></stop>
        <stop offset="0.954926" stopColor="#E37DFF"></stop>
      </radialGradient>
      <linearGradient id="paint3_linear_769_23819" x1="6.19603" y1="10.4571" x2="12.2081" y2="11.8543" gradientUnits="userSpaceOnUse">
        <stop stopColor="#7563F7" stopOpacity="0"></stop>
        <stop offset="0.986042" stopColor="#4916AE"></stop>
      </linearGradient>
      <radialGradient id="paint4_radial_769_23819" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(20.3486 19.2) rotate(-159.917) scale(25.461 21.8263)">
        <stop offset="0.221888" stopColor="#4E46E2"></stop>
        <stop offset="0.577558" stopColor="#625DF6"></stop>
        <stop offset="0.954926" stopColor="#E37DFF"></stop>
      </radialGradient>
      <linearGradient id="paint5_linear_769_23819" x1="6.19603" y1="10.4571" x2="12.2081" y2="11.8543" gradientUnits="userSpaceOnUse">
        <stop stopColor="#7563F7" stopOpacity="0"></stop>
        <stop offset="0.986042" stopColor="#4916AE"></stop>
      </linearGradient>
    </defs>
  </svg>
);

interface PromptGalleryComponentProps {
  isOpen: boolean;
  onClose: () => void;
  onPromptSelected: (prompt: string) => void;
  themeMode?: 'light' | 'dark';
  title?: string;
  dispatch?: Dispatch;
}

interface PromptGalleryButtonProps {
  onOpenGallery: () => void;
  disabled?: boolean;
  dispatch?: Dispatch;
}

type PromptGalleryTab = 'suggested' | 'personal';

const SUGGESTED_TAB: PromptGalleryTab = 'suggested';
const PERSONAL_TAB: PromptGalleryTab = 'personal';

const useStyles = makeStyles({
  modal: {
    display: 'flex',
    flexDirection: 'column',
    width: '1010px',
    maxWidth: '90vw',
    minHeight: '450px',
    maxHeight: '620px',
    padding: '28px',
    boxSizing: 'border-box',
    borderRadius: '16px',
    '@media (min-width: 1920px)': {
      width: '1264px',
      maxWidth: '92vw',
      minHeight: '560px',
      maxHeight: '780px',
      padding: '32px'
    },
    '@media (min-width: 1440px) and (max-width: 1919px)': {
      width: '1140px',
      maxWidth: '90vw',
      minHeight: '500px',
      maxHeight: '700px',
      padding: '30px'
    },
    '@media (max-width: 1366px)': {
      width: '960px',
      maxWidth: '88vw',
      minHeight: '420px',
      maxHeight: '580px',
      padding: '24px'
    },
    '@media (max-width: 1024px)': {
      width: '90vw',
      padding: '20px',
      minHeight: '380px',
      maxHeight: '540px'
    }
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '12px'
  },
  headerTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1
  },
  searchWrapper: {
    flex: '0 0 320px'
  },
  searchInput: {
    width: '100%',
    borderRadius: '4px',
    '& .fui-Input__contentBefore': {
      paddingLeft: '12px'
    },
    '& input': {
      paddingLeft: '8px'
    }
  },
  tabRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  tabs: {
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
    gap: '8px'
  },
  tab: {
    borderRadius: '999px',
    minHeight: '36px',
    padding: '4px 18px',
    border: 'none',
    backgroundColor: '#E7EBFF',
    color: '#0F6CBD',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&::after': {
      display: 'none'
    },
    '&::before': {
      display: 'none'
    },
    '&:hover': {
      backgroundColor: 'rgb(79, 107, 237) !important',
      color: '#FFFFFF !important'
    },
    '&:hover .fui-Tab__content': {
      color: '#FFFFFF !important'
    },
    '&[aria-selected="true"], &[data-selected="true"]': {
      backgroundColor: 'rgb(79, 107, 237) !important',
      color: '#FFFFFF !important'
    },
    '&[aria-selected="true"] .fui-Tab__content, &[data-selected="true"] .fui-Tab__content': {
      color: '#FFFFFF !important'
    },
    '& .fui-Tab__content': {
      fontWeight: tokens.fontWeightSemibold,
      fontSize: '14px',
      color: '#0F6CBD'
    }
  },
  activeTab: {
    backgroundColor: '#464FEB !important',
    color: '#FFFFFF !important',
    boxShadow: tokens.shadow2,
    '& .fui-Tab__content': {
      color: '#FFFFFF !important'
    }
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: 1,
    minHeight: 0
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '20px',
    paddingRight: '8px',
    maxHeight: '420px',
    overflowY: 'auto',
    '@media (min-width: 1920px)': {
      maxHeight: '520px'
    },
    '@media (min-width: 1440px) and (max-width: 1919px)': {
      maxHeight: '470px'
    },
    '@media (max-width: 1366px)': {
      maxHeight: '380px'
    },
    '@media (max-width: 1024px)': {
      maxHeight: '340px'
    },
    '@media (max-width: 1200px)': {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))'
    },
    '@media (max-width: 900px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    },
    '@media (max-width: 600px)': {
      gridTemplateColumns: 'repeat(1, minmax(0, 1fr))'
    }
  },
  card: {
    margin: '4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '24px',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    minHeight: '200px',
    cursor: 'pointer',
    transition: 'transform 0.1s ease, box-shadow 0.1s ease, border-color 0.1s ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
      border: `1px solid ${tokens.colorBrandStroke1}`
    }
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  cardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    flexShrink: 0
  },
  cardTitle: {
    margin: 0,
    fontSize: '14px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  cardDescription: {
    margin: 0,
    color: tokens.colorNeutralForeground2,
    lineHeight: '20px',
    flex: 1
  },
  cardFooter: {
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: tokens.colorNeutralForeground3
  },
  cardFooterIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    color: tokens.colorBrandForeground1
  },
  cardFooterImage: {
    width: '18px',
    height: '18px'
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    color: tokens.colorNeutralForeground3
  },
  closeButton: {
    color: tokens.colorNeutralForeground1
  },
  galleryButton: {
    color: tokens.colorNeutralForeground1,
    borderRadius: '8px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3
    }
  }
});

const dedupeByQuestion = (items: PromptGalleryItem[]): PromptGalleryItem[] => {
  const seen = new Set<string>();
  return items.filter(item => {
    const key = (item.questionText || '').trim().toLowerCase();
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const PromptGalleryButton: React.FC<PromptGalleryButtonProps> = ({
  onOpenGallery,
  disabled = false,
  dispatch
}) => {
  const styles = useStyles();
  const noOpDispatch: Dispatch = action => action;
  const telemetry = useMCAPSCopilotTelemetry(dispatch || noOpDispatch);

  const handleOpen = useCallback(() => {
    telemetry?.logQuerySubmitted({
      query: 'Prompt Gallery Button Clicked',
      component: 'PromptGalleryButton',
      action: 'click'
    });
    onOpenGallery();
  }, [telemetry, onOpenGallery]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  return (
    <TooltipHost content="Prompt Gallery">
      <Button
        appearance="transparent"
        icon={<Book20Regular />}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label="Open Prompt Gallery"
        title="Open Prompt Gallery"
        tabIndex={0}
        role="button"
        className={styles.galleryButton}
      />
    </TooltipHost>
  );
};

export const PromptGalleryComponent: React.FC<PromptGalleryComponentProps> = ({
  isOpen,
  onClose,
  onPromptSelected,
  themeMode = 'light',
  title = 'Copilot Prompt Gallery',
  dispatch
}) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<PromptGalleryTab>(SUGGESTED_TAB);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestedPrompts, setSuggestedPrompts] = useState<PromptGalleryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const noOpDispatch: Dispatch = action => action;
  const telemetry = useMCAPSCopilotTelemetry(dispatch || noOpDispatch);

  useEffect(() => {
    let isMounted = true;

    if (!isOpen) {
      return () => {
        isMounted = false;
      };
    }

    const loadData = () => {
      setIsLoading(true);
      try {
        const suggestions = promptGalleryService.getCachedData() ;//dedupeByQuestion(promptGalleryService.getCachedData());

        if (isMounted) {
          setSuggestedPrompts(suggestions);
        }
      } catch (error) {
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    setSearchTerm('');
    setActiveTab(SUGGESTED_TAB);
    loadData();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const handleSearchChange = (_event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    setSearchTerm(data.value);
  };

  const applySearch = useCallback((items: PromptGalleryItem[]) => {
    if (!searchTerm.trim()) {
      return items;
    }
    const term = searchTerm.trim().toLowerCase();
    return items.filter(item => {
      const haystack = `${item.title || ''} ${item.questionText || ''} ${item.task || ''} ${item.knowledgeArea || ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [searchTerm]);

  const filteredPrompts = useMemo(() => {
    if (activeTab === SUGGESTED_TAB) {
      return applySearch(suggestedPrompts);
    }
    return [];
  }, [activeTab, applySearch, suggestedPrompts]);

  const handleTabSelect = useCallback((_event: React.SyntheticEvent, data: { value: string | number }) => {
    const value = data.value as PromptGalleryTab;
    setActiveTab(value);
    telemetry?.logPromptGalleryAction({
      action: 'click',
      component: 'PromptGalleryComponent',
      promptPreview: `Switched to ${value}`
    });
  }, [telemetry]);

  const handlePromptClick = (prompt: PromptGalleryItem) => {
    telemetry?.logQuerySubmitted({
      query: `Prompt Selected: ${prompt.questionText?.substring(0, 50) || ''}`,
      component: 'PromptGalleryComponent',
      action: 'click'
    });
    onPromptSelected(prompt.questionText);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          telemetry?.logPromptGalleryAction({
            action: 'click',
            component: 'PromptGalleryComponent',
            promptPreview: 'Dialog dismissed'
          });
          onClose();
        }
      }}
      aria-labelledby="prompt-gallery-title"
    >
      <DialogSurface className={styles.modal} role="dialog" tabIndex={-1}>
        <FluentProvider theme={themeMode === 'dark' ? webDarkTheme : webLightTheme}>
          <div className={styles.headerRow}>
            <h2 id="prompt-gallery-title" className={styles.headerTitle}>{title}</h2>
            <div className={styles.searchWrapper}>
              <Input
                placeholder="Search for prompts"
                contentBefore={<Search24Regular />}
                value={searchTerm}
                onChange={handleSearchChange}
                className={styles.searchInput}
              />
            </div>
            <Button
              appearance="transparent"
              icon={<Dismiss24Regular />}
              aria-label="Close Prompt Gallery"
              title="Close Prompt Gallery"
              onClick={() => {
                telemetry?.logPromptGalleryAction({
                  action: 'click',
                  component: 'PromptGalleryComponent',
                  promptPreview: 'Closed via button'
                });
                onClose();
              }}
              className={styles.closeButton}
            />
          </div>

          <div className={styles.tabRow}>
            <TabList
              appearance="transparent"
              className={styles.tabs}
              selectedValue={activeTab}
              onTabSelect={handleTabSelect}
            >
              <Tab
                value={SUGGESTED_TAB}
                className={`${styles.tab} ${activeTab === SUGGESTED_TAB ? styles.activeTab : ''}`}
              >
                Suggested
              </Tab>
              {/* <Tab
                value={PERSONAL_TAB}
                className={`${styles.tab} ${activeTab === PERSONAL_TAB ? styles.activeTab : ''}`}
              >
                Your prompts
              </Tab> */}
            </TabList>
            {/* <Caption1>{`${filteredPrompts.length} prompts`}</Caption1> */}
          </div>

          <div className={styles.body}>
            {isLoading ? (
              <div className={styles.emptyState}>
                <SparkleIcon />
                <Text>Loading prompt gallery...</Text>
                <Caption1>Fetching the latest prompts</Caption1>
              </div>
            ) : filteredPrompts.length === 0 ? (
              activeTab === SUGGESTED_TAB ? (
                <div className={styles.emptyState}>
                  <SparkleIcon />
                  <Text>No prompts found</Text>
                  <Caption1>Try another search term</Caption1>
                </div>
              ) : (
                <div className={styles.grid} />
              )
            ) : (
              <div className={styles.grid}>
                {filteredPrompts.map(prompt => (
                  <div
                    key={`${prompt.questionText}-${prompt.rank}`}
                    className={styles.card}
                    onClick={() => handlePromptClick(prompt)}
                  >
                    <div className={styles.cardHeader}>
                      <span className={styles.cardIcon}>
                        <SparkleIcon />
                      </span>
                      <Text className={styles.cardTitle}>
                        {prompt.knowledgeArea || 'Knowledge area'}
                      </Text>
                    </div>
                    <Body2 className={styles.cardDescription}>
                      {prompt.questionText || prompt.title}
                    </Body2>
                    <div className={styles.cardFooter}>
                      <span className={styles.cardFooterIcon} aria-hidden="true">
                        <img
                          src="/images/mcapshelp-copilot-icon.svg"
                          alt=""
                          className={styles.cardFooterImage}
                        />
                      </span>
                      <Caption1>MCAPSHelp Copilot</Caption1>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </FluentProvider>
      </DialogSurface>
    </Dialog>
  );
};

export default PromptGalleryComponent;
